import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { q } from '../../api/queries';
import { Mono, Page, PageHeader, Panel, Skeleton, Table, col, Empty } from '@bounded/ui/primitives';
import { ErrorNotice, Provenance } from '@bounded/ui/state';
import { useFormat, useT } from '../../i18n';
import { cn } from '@bounded/ui/cn';

export const Route = createFileRoute('/_app/')({ component: Overview });

function Overview() {
  const t = useT(); const m = t.overview; const f = useFormat();
  const overview = useQuery(q.overview());
  const audit = useQuery(q.audit());
  const usage = useQuery(q.usage());
  const o = overview.data;
  const cells: { label: string; value?: number; hint?: string; to?: string; attention?: boolean }[] = [
    { label: m.accounts, value: o?.accounts, to: '/accounts' },
    { label: m.devices, value: o?.active_devices, to: '/devices' },
    { label: m.operations, value: o?.pending_operations, hint: m.operationsHint, to: '/operations', attention: !!o?.pending_operations },
    { label: m.deliveries, value: o?.pending_policy_deliveries, hint: m.deliveriesHint, attention: !!o?.pending_policy_deliveries },
  ];
  // The usage endpoint returns up to 100 connection/day rows, newest first; sum the last 7 days per connection.
  const since = new Date(Date.now() - 6 * 86_400_000).toISOString().slice(0, 10);
  const byConnection = new Map<string, number>();
  for (const row of usage.data?.items ?? []) if (row.day >= since) byConnection.set(row.connection_id, (byConnection.get(row.connection_id) ?? 0) + row.calls);
  const recent = [...(audit.data?.items ?? [])].sort((a, b) => b.created_at - a.created_at).slice(0, 6);

  return (
    <Page>
      <PageHeader title={m.title} description={m.description} />
      {overview.error ? <ErrorNotice error={overview.error} onRetry={() => void overview.refetch()} /> : (
        <section className="grid gap-2">
          <div className="grid overflow-hidden rounded-[var(--radius-panel)] border border-rule bg-rule gap-px sm:grid-cols-2 lg:grid-cols-4">
            {cells.map((c) => {
              const body = <>
                <p className="text-sm text-graphite">{c.label}</p>
                {c.value === undefined ? <Skeleton className="mt-1 h-8 w-16" /> : <p className={cn('text-2xl font-semibold tabular-nums', c.attention && 'text-ask')}>{f.number(c.value)}</p>}
                {c.hint && <p className="mt-1 text-sm text-graphite">{c.hint}</p>}
              </>;
              return c.to
                ? <Link key={c.label} to={c.to} className="bg-surface px-4 py-3.5 hover:bg-paper">{body}</Link>
                : <div key={c.label} className="bg-surface px-4 py-3.5">{body}</div>;
            })}
          </div>
          {o && <Provenance>{m.sampled(f.dateTime(o.sampled_at))}</Provenance>}
        </section>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
        <Panel title={m.recent} actions={<Link to="/audit" className="text-sm text-boundary hover:underline">{m.allAudit}</Link>}>
          {audit.error ? <div className="p-4"><ErrorNotice error={audit.error} onRetry={() => void audit.refetch()} /></div>
            : !audit.data ? <div className="p-4"><Skeleton className="h-24" /></div>
            : !recent.length ? <Empty title={m.recentEmpty} />
            : <ul>{recent.map((e) => (
                <li key={e.id} className="grid gap-0.5 border-b border-rule px-4 py-3 last:border-b-0">
                  <p className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="font-medium">{t.audit.actions[e.action] ?? e.action} <Mono value={e.target_id} className="font-normal text-graphite" /></span>
                    <span className="text-sm text-graphite">{f.ago(e.created_at)}</span>
                  </p>
                  <p className="text-sm text-graphite [overflow-wrap:anywhere]">{e.reason}</p>
                </li>
              ))}</ul>}
        </Panel>
        <Panel title={m.usage} actions={<Link to="/models" className="text-sm text-boundary hover:underline">{m.allUsage}</Link>}>
          {usage.error ? <div className="p-4"><ErrorNotice error={usage.error} onRetry={() => void usage.refetch()} /></div>
            : !usage.data ? <div className="p-4"><Skeleton className="h-16" /></div>
            : !byConnection.size ? <Empty title={m.usageEmpty} />
            : <Table minWidth="16rem" head={[{ label: t.models.connection }, { label: t.models.calls, className: col.num }]}>
                {[...byConnection].map(([id, calls]) => <tr key={id}><td><Mono value={id} /></td><td className={col.num}>{f.number(calls)}</td></tr>)}
              </Table>}
        </Panel>
      </div>
    </Page>
  );
}
